import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchRestaurantVendorOrders } from "../../actions/restaurantVendorOrdersActions";
import { useHistory } from "react-router-dom";
import {
  PaymentBadge,
  DeliveryStatusBadge,
} from "../../components/OrderStatusBadges";
import { THEME as C } from "../../components/dashboardTheme";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  makeStyles,
  TablePagination,
  Chip,
  Box,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Card,
  Switch,
  InputBase,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import { recall, remember } from "../../utilities/identityMemory";
import { socket } from "../../utilities/socket";
import useVendorOrderRooms from "../../hooks/useVendorOrderRooms";
import { useBranchScope } from "../../context/BranchScopeContext";
import { vendorOwnerIdOf } from "../../utilities/vendorScope";
import useOrderListFilters from "../../hooks/useOrderListFilters";
import { DELIVERY_STATE_CFG } from "../../utilities/paymentStatus";
import "../../components/Orders/OrderListFilterBar.css";

/**
 * Restaurant Orders — the vendor's order list.
 * ────────────────────────────────────────────────────────────────────────────
 * This screen used to be one of the last on the old look: a bare `h4`, a
 * half-empty filter strip, MUI chips carrying `0 2px 8px rgba(0,0,0,0.8)` drop
 * shadows, and a 1024px empty-state image that is actually a WebP with the
 * transparency checkerboard painted into its pixels. It now follows
 * `components/dashboardTheme.js` like every other new screen, and it renders
 * delivery state through the shared `DeliveryStatusBadge` instead of its own
 * five-case colour switch — that switch silently dropped PENDING_3PL, ACCEPTED,
 * PICKED_UP, IN_TRANSIT, ARRIVED, FULFILLED and NOT_APPLICABLE onto an unstyled
 * grey chip showing the raw enum.
 *
 * What is genuinely NEW here, beyond paint:
 *   · A summary strip (needs action / on the way / completed / value) computed
 *     from the rows actually on screen, so it never disagrees with the table.
 *   · A search box and a status filter. Both are page-scoped, exactly like the
 *     location filters already were, and the strip says so out loud
 *     ("Showing 4 of 9 on this page") rather than letting a vendor believe an
 *     empty result means an empty business.
 *   · A distinct empty state for "filters hide everything". Previously that
 *     case rendered a table header with no rows under it and no explanation.
 *
 * The filter state machine, its localStorage persistence, the socket wiring and
 * the pagination contract are unchanged — this is a presentation pass.
 */

/* Cohesive location colour system — matches the Store column in the table:
   Main Store = red, outlets = teal, All Locations = navy. Selected chips are
   solid; unselected use an 8% tint + hairline border and NO heavy drop shadow. */
const LOCATION_RED = "#A62B1F";
const LOCATION_TEAL = "#193C40";
const LOCATION_NAVY = "#013A63";
const chipStyle = (color, active) => ({
  backgroundColor: active ? color : `${color}14`,
  color: active ? "#fff" : color,
  fontWeight: 600,
  border: active ? "1px solid transparent" : `1px solid ${color}33`,
  boxShadow: "none",
});

/* Delivery states grouped the way a kitchen actually thinks about them. Kept in
   sync with DELIVERY_STATE_CFG in utilities/paymentStatus.js. */
// Saved outlet filter, per signed-in identity (see saveFilterState).
const OUTLET_FILTERS_KEY = "lusuku.restaurantOrders.outletFilters";
const SELECTED_OUTLET_KEY = "lusuku.restaurantOrders.selectedOutlet";

const NEEDS_ACTION = ["PROCESSING", "PENDING_3PL"];
const IN_FLIGHT = [
  "ASSIGNED",
  "ACCEPTED",
  "PICKED_UP",
  "DISPATCHED",
  "IN_TRANSIT",
  "ARRIVED",
];
const COMPLETED = ["DELIVERED", "FULFILLED"];

/* The status filter is a SERVER filter (?delivery=, utils/orderListFilters.js)
   so it cuts across pages, and it lives in the URL so the dashboard cards can
   open this list on exactly the status they counted (utilities/
   orderDeepLinks.js): the four single-status entries are the cards' own
   definitions, the groups are how a kitchen reads the board. */
const STATUS_FILTERS = [
  { key: "all", label: "Any status", match: null },
  { key: "action", label: "Needs action", match: NEEDS_ACTION },
  { key: "processing", label: "New (processing)", match: ["PROCESSING"] },
  { key: "assigned", label: "Assigned to a rider", match: ["ASSIGNED"] },
  { key: "flight", label: "On the way", match: IN_FLIGHT },
  { key: "dispatched", label: "Dispatched", match: ["DISPATCHED"] },
  { key: "done", label: "Completed", match: COMPLETED },
  { key: "delivered", label: "Delivered", match: ["DELIVERED"] },
  { key: "rejected", label: "Rejected", match: ["REJECTED"] },
];
const CUSTOM_STATUS_KEY = "custom";
const sameSet = (a, b) => a.length === b.length && a.every((v) => b.includes(v));
/* The Select key for the status list in the URL: a known entry, "all" for
   none, or a one-off entry named after the statuses. */
const statusKeyFor = (delivery) => {
  if (!delivery.length) return "all";
  return STATUS_FILTERS.find((s) => s.match && sameSet(s.match, delivery))?.key || CUSTOM_STATUS_KEY;
};
const statusLabelOf = (value) => DELIVERY_STATE_CFG[value]?.label || value;

const norm = (v) => String(v ?? "").trim().toUpperCase();

/* Order ids are 24-char ObjectIds. Printing one in full pushed every other
   column right and told the vendor nothing; the last six characters are what
   staff read out over the counter anyway. Full id stays on the tooltip and on
   the clipboard. */
const shortId = (id) => `#${String(id || "").slice(-6).toUpperCase()}`;

/* "Today 14:32" beats "9/7/2026, 2:32:11 PM" for the only question a vendor asks
   of this column: how long has this been sitting there. */
const relativeDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return `Today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return `Yesterday ${time}`;
  return `${d.toLocaleDateString([], { day: "numeric", month: "short" })} ${time}`;
};

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(0, 2),
    backgroundColor: "transparent",
  },

  /* ── Header band ────────────────────────────────────────────────────── */
  headerBand: {
    background: `linear-gradient(135deg, ${C.header} 0%, ${C.headerTo} 100%)`,
    borderRadius: C.radius,
    boxShadow: C.headerShadow,
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  /* A single soft highlight keeps the band from reading as a flat rectangle.
     It is decorative only and sits behind the text. */
  headerGlow: {
    position: "absolute",
    top: -90,
    right: -40,
    width: 260,
    height: 260,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 16, zIndex: 1 },
  headerMark: {
    width: 46,
    height: 46,
    borderRadius: 12,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 19,
    flexShrink: 0,
  },
  headerEyebrow: {
    color: "rgba(255,255,255,0.62)",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    lineHeight: 1.4,
  },
  headerTitle: {
    color: "#fff",
    fontSize: "1.4rem",
    fontWeight: 600,
    letterSpacing: "-0.015em",
    lineHeight: 1.25,
  },
  headerSub: {
    color: "rgba(255,255,255,0.66)",
    fontSize: "0.8rem",
    marginTop: 2,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    zIndex: 1,
  },
  livePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "5px 12px",
    borderRadius: 20,
    background: "rgba(255,255,255,0.10)",
    border: "1px solid rgba(255,255,255,0.16)",
    color: "rgba(255,255,255,0.9)",
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#3ddc97",
    boxShadow: "0 0 0 0 rgba(61,220,151,0.7)",
    animation: "$pulse 2.4s ease-out infinite",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  liveDotOff: { background: "#f0a020", animation: "none", boxShadow: "none" },
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(61,220,151,0.55)" },
    "70%": { boxShadow: "0 0 0 7px rgba(61,220,151,0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(61,220,151,0)" },
  },
  rememberLabel: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.78)",
    fontWeight: 500,
    userSelect: "none",
  },
  headerSwitch: {
    "& .MuiSwitch-colorSecondary.Mui-checked": { color: "#3ddc97" },
    "& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#3ddc97",
    },
    "& .MuiSwitch-track": { backgroundColor: "rgba(255,255,255,0.35)" },
  },

  /* ── Summary strip ──────────────────────────────────────────────────── */
  statRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  statCard: {
    background: C.panel,
    border: `1px solid ${C.border}`,
    borderRadius: C.radius,
    boxShadow: C.shadow,
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    transition: "transform 160ms cubic-bezier(0.4,0,0.2,1), box-shadow 160ms",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(26,43,74,0.12)",
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  },
  statIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    flexShrink: 0,
  },
  statValue: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: C.text,
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
    fontVariantNumeric: "tabular-nums",
  },
  statLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: C.textLight,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginTop: 2,
  },

  /* ── Filter bar ─────────────────────────────────────────────────────── */
  filterCard: {
    marginTop: theme.spacing(2),
    background: C.panel,
    border: `1px solid ${C.border}`,
    borderRadius: C.radius,
    boxShadow: C.shadow,
    overflow: "visible",
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: "12px 16px",
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filterTitle: {
    fontSize: "0.68rem",
    color: C.textLight,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    flexShrink: 0,
  },
  formControl: {
    minWidth: 190,
    "& .MuiOutlinedInput-root": {
      borderRadius: C.radiusSm,
      background: C.stripBg,
      transition: "background 160ms ease, box-shadow 160ms ease",
      "& fieldset": { borderColor: C.border },
      "&:hover fieldset": { borderColor: "#cbd5e1" },
      "&.Mui-focused": { boxShadow: `0 0 0 3px ${C.blue}22` },
      "&.Mui-focused fieldset": { borderColor: C.blue },
    },
    "& .MuiSelect-select": { paddingTop: 9, paddingBottom: 9 },
  },
  statusControl: { minWidth: 150 },
  checkboxGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(0.5),
    overflowX: "auto",
    "& .MuiFormControlLabel-root": {
      margin: 0,
      padding: "3px 10px 3px 4px",
      borderRadius: C.radiusSm,
      transition: "background 160ms ease",
      "&:hover": { backgroundColor: C.stripBg },
    },
    "&::-webkit-scrollbar": { height: 4 },
    "&::-webkit-scrollbar-track": { background: "#f1f5f9", borderRadius: 4 },
    "&::-webkit-scrollbar-thumb": {
      background: "#cbd5e1",
      borderRadius: 4,
      "&:hover": { background: "#94a3b8" },
    },
  },
  /* Search sits hard right so the bar reads as one balanced line instead of a
     cluster of controls with a dead half. */
  searchWrap: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: C.stripBg,
    border: `1px solid ${C.border}`,
    borderRadius: C.radiusSm,
    padding: "6px 12px",
    minWidth: 230,
    transition: "box-shadow 160ms ease, border-color 160ms ease",
    "&:focus-within": {
      borderColor: C.blue,
      boxShadow: `0 0 0 3px ${C.blue}22`,
      background: C.panel,
    },
    [theme.breakpoints.down("xs")]: { marginLeft: 0, width: "100%" },
  },
  searchInput: { fontSize: "0.85rem", color: C.text, flex: 1 },
  clearSearch: {
    border: "none",
    background: "transparent",
    color: C.textLight,
    cursor: "pointer",
    padding: 2,
    lineHeight: 1,
    fontSize: 12,
    "&:hover": { color: C.text },
  },
  resultLine: {
    borderTop: `1px solid ${C.border}`,
    background: C.stripBg,
    padding: "8px 16px",
    fontSize: "0.74rem",
    color: C.textLight,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottomLeftRadius: C.radius,
    borderBottomRightRadius: C.radius,
    flexWrap: "wrap",
  },
  resetLink: {
    border: "none",
    background: "transparent",
    color: C.blue,
    fontWeight: 600,
    fontSize: "0.74rem",
    cursor: "pointer",
    padding: 0,
    "&:hover": { textDecoration: "underline" },
  },
  orderCount: {
    backgroundColor: "#eef2f7",
    color: C.textMid,
    padding: "1px 7px",
    borderRadius: 10,
    fontSize: "0.68rem",
    fontWeight: 700,
    marginLeft: 6,
    fontVariantNumeric: "tabular-nums",
    transition: "all 160ms ease",
  },
  selectedOrderCount: { backgroundColor: C.teal, color: "#fff" },
  menuItem: {
    borderRadius: C.radiusSm,
    margin: "2px 4px",
    "&.Mui-selected": {
      backgroundColor: C.blueSoft,
      "&:hover": { backgroundColor: "#dceafe" },
    },
  },
  locationChip: { borderRadius: C.radiusSm, fontWeight: 600 },
  multipleSelected: { display: "flex", flexWrap: "wrap", gap: 4 },

  /* ── Table ──────────────────────────────────────────────────────────── */
  tableWrap: {
    marginTop: theme.spacing(2),
    border: `1px solid ${C.border}`,
    borderRadius: C.radius,
    boxShadow: C.shadow,
    overflow: "hidden",
  },
  tableContainer: { maxHeight: "calc(100vh - 400px)" },
  table: { minWidth: 980, borderCollapse: "separate" },
  tableHead: { background: C.header },
  /* The head is sticky, so each CELL must carry the background itself — a
     gradient on the row would scroll out from under the cells and let table
     rows show through the header. Flat navy, matching the band's base colour. */
  tableHeadCell: {
    color: "rgba(255,255,255,0.86)",
    fontWeight: 700,
    fontSize: "0.68rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${C.headerTo}`,
    padding: "13px 14px",
    whiteSpace: "nowrap",
    backgroundColor: C.header,
  },
  tableRow: {
    transition: "background 140ms ease",
    "&:hover": { backgroundColor: "#f7f9fc" },
    "&:last-child td": { borderBottom: "none" },
  },
  cell: {
    fontSize: "0.82rem",
    color: C.text,
    borderBottom: `1px solid ${C.border}`,
    padding: "11px 14px",
    verticalAlign: "middle",
  },
  /* A 3px bar in the location colour lets a vendor scan main-store vs outlet
     rows without reading the Store column at all. */
  accentCell: { position: "relative", paddingLeft: 18 },
  accentBar: {
    position: "absolute",
    left: 0,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: "0 3px 3px 0",
  },
  idButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    fontFamily:
      "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: C.text,
    letterSpacing: "0.02em",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    "&:hover": { color: C.blue },
    "&:focus-visible": {
      outline: `2px solid ${C.blue}`,
      outlineOffset: 2,
      borderRadius: 3,
    },
  },
  copiedTag: {
    fontSize: "0.62rem",
    fontWeight: 700,
    color: C.teal,
    letterSpacing: "0.04em",
  },
  customerCell: { display: "flex", alignItems: "center", gap: 9 },
  avatar: {
    width: 27,
    height: 27,
    borderRadius: "50%",
    background: C.blueSoft,
    color: C.blueDeep,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.7rem",
    fontWeight: 700,
    flexShrink: 0,
    textTransform: "uppercase",
  },
  money: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
    color: C.text,
    whiteSpace: "nowrap",
  },
  moneyUnit: {
    fontSize: "0.68rem",
    color: C.textLight,
    fontWeight: 600,
    marginRight: 3,
  },
  addressCell: {
    maxWidth: 210,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: C.textMid,
  },
  methodPill: {
    display: "inline-block",
    padding: "2px 9px",
    borderRadius: 20,
    background: C.stripBg,
    border: `1px solid ${C.border}`,
    color: C.textMid,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  },
  dateCell: { color: C.textMid, whiteSpace: "nowrap", fontSize: "0.78rem" },
  manageBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 13px",
    borderRadius: C.radiusSm,
    border: `1px solid ${C.border}`,
    background: C.panel,
    color: C.text,
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 160ms cubic-bezier(0.4,0,0.2,1)",
    "&:hover": {
      background: C.header,
      borderColor: C.header,
      color: "#fff",
      transform: "translateY(-1px)",
    },
    "&:focus-visible": { outline: `2px solid ${C.blue}`, outlineOffset: 2 },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  },
  pagination: {
    borderTop: `1px solid ${C.border}`,
    background: C.stripBg,
    "& .MuiTablePagination-caption": {
      fontSize: "0.76rem",
      color: C.textMid,
    },
  },

  /* ── Empty + loading states ─────────────────────────────────────────── */
  emptyPanel: {
    marginTop: theme.spacing(2),
    background: C.panel,
    border: `1px solid ${C.border}`,
    borderRadius: C.radius,
    boxShadow: C.shadow,
    padding: "56px 24px 60px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  emptyTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: C.text,
    marginTop: 26,
    letterSpacing: "-0.01em",
  },
  emptyBody: {
    fontSize: "0.86rem",
    color: C.textLight,
    marginTop: 7,
    maxWidth: 400,
    lineHeight: 1.6,
  },
  emptyPrimary: {
    marginTop: 22,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    borderRadius: C.radiusSm,
    border: "none",
    background: `linear-gradient(135deg, ${C.header} 0%, ${C.headerTo} 100%)`,
    color: "#fff",
    fontSize: "0.84rem",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 10px rgba(26,43,74,0.24)",
    transition: "transform 160ms cubic-bezier(0.4,0,0.2,1), box-shadow 160ms",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 18px rgba(26,43,74,0.30)",
    },
    "&:focus-visible": { outline: `2px solid ${C.blue}`, outlineOffset: 3 },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  },
  emptyGhost: {
    marginTop: 20,
    padding: "9px 18px",
    borderRadius: C.radiusSm,
    border: `1px solid ${C.border}`,
    background: C.panel,
    color: C.text,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 160ms ease",
    "&:hover": { background: C.stripBg },
    "&:focus-visible": { outline: `2px solid ${C.blue}`, outlineOffset: 2 },
  },
  channelRow: {
    display: "flex",
    gap: 8,
    marginTop: 24,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  channelPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 11px",
    borderRadius: 20,
    background: C.stripBg,
    border: `1px solid ${C.border}`,
    color: C.textMid,
    fontSize: "0.7rem",
    fontWeight: 600,
  },
  skelRow: {
    height: 46,
    borderBottom: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "0 14px",
  },
  skelBar: {
    height: 9,
    borderRadius: 5,
    background:
      "linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%)",
    backgroundSize: "400% 100%",
    animation: "$shimmer 1.4s ease infinite",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0 50%" },
  },
  errorAlert: { marginTop: theme.spacing(3), borderRadius: C.radius },
}));

/* Empty-state artwork. Inline SVG rather than the old 1024px raster: it is
   ~2KB, sharp on any display, and inherits the theme palette. The previous
   asset was a WebP named .png whose "transparency" checkerboard was painted
   into the image, so it rendered as literal grey squares on a white panel. */
const EmptyOrdersArt = () => (
  <svg width="132" height="132" viewBox="0 0 132 132" role="presentation" focusable="false">
    <defs>
      <linearGradient id="lsk-slip" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#f4f7fc" />
      </linearGradient>
      <radialGradient id="lsk-halo" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={C.blue} stopOpacity="0.13" />
        <stop offset="100%" stopColor={C.blue} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="66" cy="66" r="66" fill="url(#lsk-halo)" />
    <g transform="translate(37 22)">
      {/* Receipt with a torn bottom edge — the shape of an order slip. */}
      <path
        d="M4 3a3 3 0 013-3h44a3 3 0 013 3v78l-5.9-4.4a2 2 0 00-2.4 0L39.8 81l-5.9-4.4a2 2 0 00-2.4 0L25.6 81l-5.9-4.4a2 2 0 00-2.4 0L11.4 81 5.5 76.6A2 2 0 004 78.2z"
        fill="url(#lsk-slip)"
        stroke={C.border}
        strokeWidth="1.5"
      />
      <rect x="14" y="17" width="34" height="5" rx="2.5" fill="#dbe3ee" />
      <rect x="14" y="30" width="26" height="4" rx="2" fill="#e6ebf3" />
      <rect x="14" y="41" width="30" height="4" rx="2" fill="#e6ebf3" />
      <rect x="14" y="52" width="20" height="4" rx="2" fill="#e6ebf3" />
    </g>
    {/* Steam curls: this is a kitchen, and the panel should feel like one. */}
    <g stroke={C.teal} strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.5">
      <path d="M96 44c4-4 0-8 4-12" />
      <path d="M106 50c4-4 0-8 4-12" />
    </g>
    <circle cx="30" cy="34" r="4" fill={C.teal} opacity="0.28" />
    <circle cx="104" cy="88" r="6" fill={C.blue} opacity="0.16" />
  </svg>
);

const FilteredEmptyArt = () => (
  <svg width="112" height="112" viewBox="0 0 112 112" role="presentation" focusable="false">
    <defs>
      <radialGradient id="lsk-halo2" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={C.amber} stopOpacity="0.16" />
        <stop offset="100%" stopColor={C.amber} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="56" cy="56" r="56" fill="url(#lsk-halo2)" />
    <path
      d="M34 34h44L60 57v22l-8-6V57z"
      fill="#ffffff"
      stroke={C.border}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="76" cy="72" r="14" fill="#fff" stroke={C.amber} strokeWidth="2.5" />
    <path d="M70 72h12" stroke={C.amber} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const RestaurantOrderTable = () => {
  const classes = useStyles();
  const [vendorId, setVendorId] = useState(null);
  const [fetchComplete, setFetchComplete] = useState(false);
  const dispatch = useDispatch();
  const { orders, loading, error, currentPage, numberOfPages } = useSelector(
    (state) => state.restaurantVendorOrders
  );
  const history = useHistory();
  const [selectedOutlet, setSelectedOutlet] = useState("all");
  const [filters, setFilters] = useState({
    mainStore: true,
  });
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [search, setSearch] = useState("");
  // Status and payment method: in the URL, applied by the server (see
  // STATUS_FILTERS above). The Select reads and writes the URL.
  const listFilters = useOrderListFilters();
  const statusFilter = statusKeyFor(listFilters.delivery);
  const setStatusFilter = (key) =>
    listFilters.setDelivery(STATUS_FILTERS.find((s) => s.key === key)?.match || []);
  const [copiedId, setCopiedId] = useState(null);
  const [live, setLive] = useState(socket?.connected ?? false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [persistFilters, setPersistFilters] = useState(() => {
    return localStorage.getItem("persistOutletFilters") === "true";
  });
  // Guards the one-time filter initialisation so later orders refreshes
  // (socket updates / pagination) don't reset the user's active selection.
  const didInitFilters = useRef(false);

  // `shouldPersist` is passed explicitly so callers that ENABLE persistence in
  // the same tick can save immediately — reading the `persistFilters` state here
  // would see its stale (pre-setState) value and silently skip the write.
  //
  // The saved filter names THIS restaurant's outlets, so it is kept per signed-in
  // identity (utilities/identityMemory.js). Under one browser-wide key, one
  // restaurant's outlet selection was applied to the next restaurant signed in on
  // the same browser (an admin's "Sign in as vendor" included), whose orders all
  // failed to match it and the table read "no orders". The on/off switch itself
  // stays browser-wide: it names nothing.
  const saveFilterState = (newFilters, newSelectedOutlet, shouldPersist = persistFilters) => {
    if (shouldPersist) {
      remember(OUTLET_FILTERS_KEY, newFilters, { storage: "local" });
      remember(SELECTED_OUTLET_KEY, newSelectedOutlet, { storage: "local" });
    }
  };

  const user = getUserFromLocalStorage();

  // Branches (4 Oct 2026): the server filters by the branch picked in the
  // navbar (no outletId = every location this login may see), and the
  // location chips list the vendor's locations from /locations rather than
  // only the outlets that happen to be on this page.
  const { outletIdParam, locations: directoryLocations, isBranchScoped, activeLocation } = useBranchScope();
  const bookOpts = useMemo(
    () => ({ ...(outletIdParam ? { outletId: outletIdParam } : {}), ...listFilters.filters }),
    [outletIdParam, listFilters.filters]
  );
  // One branch picked in the navbar (or a member who runs one branch): every
  // order on this page is from it, so the location filter would only offer
  // empty choices. It is replaced by a line naming the branch.
  const pinned = outletIdParam !== undefined;
  // A member whose branches leave out the main store is not offered it.
  const showMain = !isBranchScoped || (directoryLocations || []).some((l) => l.isMain);

  useEffect(() => {
    if (user) {
      // The owner's id, also for a team member (it named the member before,
      // and the list came back empty).
      setVendorId(vendorOwnerIdOf(user));
    }
  }, [user]);

  const fetchData = async (page = currentPage) => {
    if (vendorId) {
      await dispatch(fetchRestaurantVendorOrders(vendorId, page, 10, bookOpts));
      setFetchComplete(true);
    }
  };

  // A changed status or payment-method filter restarts at page 1: the page
  // the operator was on may not exist for the narrower list.
  const lastFilterKey = useRef(listFilters.filterKey);
  useEffect(() => {
    const filterChanged = lastFilterKey.current !== listFilters.filterKey;
    lastFilterKey.current = listFilters.filterKey;
    fetchData(filterChanged ? 1 : currentPage);
  }, [dispatch, vendorId, currentPage, listFilters.filterKey]);

  const getUniqueOutlets = () => {
    const uniqueOutlets = new Map();
    // Every branch this login may see, named as the vendor named it.
    (directoryLocations || [])
      .filter((l) => !l.isMain)
      .forEach((l) => uniqueOutlets.set(l.id, { _id: l.id, name: l.name }));
    if (!orders) return Array.from(uniqueOutlets.values());

    orders.forEach((order) => {
      if (order.displayLocation && order.displayLocation !== "mainStore") {
        const firstItem = order.orderItems[0];
        if (firstItem?.outletId) {
          uniqueOutlets.set(order.displayLocation, {
            _id: firstItem.outletId._id,
            name: firstItem.outletId.name,
          });
        }
      }
    });

    return Array.from(uniqueOutlets.values());
  };

  const getDeliveryAddress = (order) => {
    const { shippingAddress } = order;
    if (!shippingAddress) return "N/A";

    // Check specific address field inside address object
    if (shippingAddress.address?.address) {
      return shippingAddress.address.address;
    }

    // Check if address is directly a string
    if (typeof shippingAddress.address === "string") {
      return shippingAddress.address;
    }

    // Check locationName
    if (shippingAddress.locationName?.name) {
      return shippingAddress.locationName.name;
    }

    return "N/A";
  };

  const getCustomerName = (order) => {
    // Name → delivery phone → client name/phone → "—".
    // Legacy orders (pre pre-save-hook) have a blank customerName; the phone is
    // always present, so surface it.
    return (
      (order.customerName && String(order.customerName).trim()) ||
      (order.shippingAddress?.customerNumber &&
        String(order.shippingAddress.customerNumber).trim()) ||
      (order.client_id?.name && String(order.client_id.name).trim()) ||
      order.client_id?.phoneNumber ||
      order.client_id?.mobile ||
      "—"
    );
  };

  /* Location filter — unchanged semantics. Search is applied on top and is
     deliberately NOT persisted: a saved location preference is a standing
     choice, a search term is not. Status is no longer filtered here: the
     server already applied it before paging. */
  const filterOrders = () => {
    if (!orders || (!pinned && selectedOutlet === "")) return [];

    const term = search.trim().toLowerCase();

    return orders.filter((order) => {
      let locationOk;
      if (pinned) {
        // The server already sent this branch's orders only.
        locationOk = true;
      } else if (selectedOutlet === "all") {
        // Check if order's location is enabled in filters
        if (order.displayLocation === "mainStore") {
          locationOk = filters.mainStore;
        } else {
          locationOk = filters[order.displayLocation] || false;
        }
      } else {
        // Match specific location
        if (selectedOutlet === "mainStore") {
          locationOk = order.displayLocation === "mainStore";
        } else {
          locationOk = order.displayLocation === selectedOutlet;
        }
      }
      if (!locationOk) return false;

      if (term) {
        const haystack = [
          order._id,
          getCustomerName(order),
          getDeliveryAddress(order),
          order.paymentMethod,
          order.displayLocationName,
          order.orderItems?.[0]?.outletId?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      return true;
    });
  };

  useEffect(() => {
    setFilteredOrders(filterOrders());
  }, [orders, selectedOutlet, filters, search, pinned]);

  const handleFilterChange = (event) => {
    const { name, checked } = event.target;

    const newFilters = {
      ...filters,
      [name]: checked,
    };

    // Derive the dropdown value from the NEW filters and keep it in a local so
    // both the state update and the persisted snapshot use the fresh value
    // (reading `selectedOutlet` here would persist the stale, pre-change value).
    const willHaveCheckedBoxes = Object.values(newFilters).some(
      (value) => value
    );

    let newSelectedOutlet;
    if (!willHaveCheckedBoxes) {
      // If no checkboxes will be checked, clear the dropdown selection
      newSelectedOutlet = "";
    } else {
      // Count total possible checkboxes and how many are checked
      const totalOutlets = getUniqueOutlets().length + 1; // +1 for main store
      const checkedFilters = Object.entries(newFilters).filter(
        ([_, value]) => value
      );

      if (checkedFilters.length === totalOutlets) {
        // Only set to "all" if ALL checkboxes are checked
        newSelectedOutlet = "all";
      } else {
        // One (or several-but-not-all) checked → show the first checked option
        newSelectedOutlet = checkedFilters[0][0];
      }
    }

    setSelectedOutlet(newSelectedOutlet);
    setFilters(newFilters);
    saveFilterState(newFilters, newSelectedOutlet);
  };

  const handleOutletChange = (event) => {
    const newSelectedOutlet = event.target.value;
    setSelectedOutlet(newSelectedOutlet);

    // Update checkboxes based on dropdown selection
    const uniqueOutlets = getUniqueOutlets();
    let newFilters;

    if (newSelectedOutlet === "all") {
      // When "All Locations" is selected, check all checkboxes
      newFilters = {
        mainStore: true,
        ...uniqueOutlets.reduce(
          (acc, outlet) => ({
            ...acc,
            [outlet._id]: true,
          }),
          {}
        ),
      };
    } else {
      // When a specific location is selected, only check that checkbox
      newFilters = {
        mainStore: false,
        ...uniqueOutlets.reduce(
          (acc, outlet) => ({
            ...acc,
            [outlet._id]: false,
          }),
          {}
        ),
      };

      if (newSelectedOutlet === "mainStore") {
        newFilters.mainStore = true;
      } else {
        newFilters[newSelectedOutlet] = true;
      }
    }

    setFilters(newFilters);
    saveFilterState(newFilters, newSelectedOutlet);
  };

  // Single initialiser for the location filters. On the FIRST load with orders,
  // it restores the persisted selection (when "Remember My Selections" is on and
  // something was saved) or defaults to all-checked. On later refreshes (socket
  // updates / pagination) it must NOT clobber the user's current selection — it
  // only merges in outlets that appeared for the first time (default checked).
  useEffect(() => {
    if (!orders || orders.length === 0) return;
    const uniqueOutlets = getUniqueOutlets();
    const allChecked = () => ({
      mainStore: true,
      ...uniqueOutlets.reduce(
        (acc, outlet) => ({ ...acc, [outlet._id]: true }),
        {}
      ),
    });

    if (!didInitFilters.current) {
      if (persistFilters) {
        const savedFilters = recall(OUTLET_FILTERS_KEY, { storage: "local" });
        const savedOutlet = recall(SELECTED_OUTLET_KEY, { storage: "local" });
        if (savedFilters) {
          setFilters(savedFilters);
          if (savedOutlet !== null) setSelectedOutlet(String(savedOutlet));
        } else {
          setFilters(allChecked());
        }
      } else {
        setFilters(allChecked());
      }
      didInitFilters.current = true;
    } else {
      // Preserve the active selection; only add newly-seen outlets (default on).
      setFilters((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const outlet of uniqueOutlets) {
          if (!(outlet._id in next)) {
            next[outlet._id] = true;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  }, [orders, persistFilters]);

  const getStoreCounts = () => {
    if (!orders) return {};

    const counts = {
      mainStore: 0,
    };

    orders.forEach((order) => {
      // Each row is one order at one location — count ORDERS per location, not
      // items. (Previously summed order.orderItems.length, so a multi-item order
      // inflated the badge and it no longer matched the rows shown when filtered.)
      if (order.displayLocation === "mainStore") {
        counts.mainStore += 1;
      } else if (order.displayLocation) {
        counts[order.displayLocation] =
          (counts[order.displayLocation] || 0) + 1;
      }
    });

    return counts;
  };

  const handlePersistToggle = (event) => {
    const newPersistState = event.target.checked;
    setPersistFilters(newPersistState);
    localStorage.setItem("persistOutletFilters", newPersistState);

    if (!newPersistState) {
      // Clear saved filters when turning off persistence
      remember(OUTLET_FILTERS_KEY, null, { storage: "local" });
      remember(SELECTED_OUTLET_KEY, null, { storage: "local" });

      // Reset to default state (all selected)
      const uniqueOutlets = getUniqueOutlets();
      const defaultFilters = {
        mainStore: true,
        ...uniqueOutlets.reduce(
          (acc, outlet) => ({
            ...acc,
            [outlet._id]: true,
          }),
          {}
        ),
      };
      setFilters(defaultFilters);
      setSelectedOutlet("all");
    } else {
      // Save current state when turning ON persistence. Pass shouldPersist=true
      // explicitly — `persistFilters` state is still false in this tick.
      saveFilterState(filters, selectedOutlet, true);
    }
  };

  // (Persist preference is read synchronously in the persistFilters useState
  // initialiser; saved filters are restored by the one-time initialiser effect
  // above once orders load — so no separate mount effect is needed here.)

  // One join for every order room: the server picks the whole business or
  // this member's branches. The old room join is the fallback for a server
  // older than this page.
  useVendorOrderRooms(socket, vendorId, {
    fallback: (s) => s.emit("join", `restaurant:${vendorId}`),
  });

  useEffect(() => {
    if (!vendorId) return;

    const refresh = () => {
      dispatch(fetchRestaurantVendorOrders(vendorId, currentPage, 10, bookOpts));
      setLastUpdate(new Date());
    };

    // Listen for new orders
    socket.on("newRestaurantOrder", refresh);

    // Listen for order updates
    socket.on("orderUpdated", refresh);

    // Listen for status changes
    socket.on("orderStatusChanged", refresh);

    // The header's live pill reflects the real socket state rather than always
    // claiming "Live" — a vendor watching a dead socket is the worst case here.
    const onConnect = () => setLive(true);
    const onDisconnect = () => setLive(false);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    setLive(socket?.connected ?? false);

    return () => {
      socket.off("newRestaurantOrder", refresh);
      socket.off("orderUpdated", refresh);
      socket.off("orderStatusChanged", refresh);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      // No leave: the shared socket serves every screen, and the rooms are
      // what the navbar's alerts listen on too.
    };
  }, [vendorId, currentPage, dispatch, bookOpts]);

  /* Summary figures come from the ROWS ON SCREEN, so the strip can never
     disagree with the table under it. */
  const stats = useMemo(() => {
    const rows = filteredOrders || [];
    const countIn = (list) =>
      rows.filter((o) => list.includes(norm(o.delivery_status))).length;
    return {
      action: countIn(NEEDS_ACTION),
      flight: countIn(IN_FLIGHT),
      done: countIn(COMPLETED),
      value: rows
        .filter((o) => norm(o.delivery_status) !== "REJECTED")
        .reduce((sum, o) => sum + (Number(o.totalPrice) || 0), 0),
    };
  }, [filteredOrders]);

  const outlets = getUniqueOutlets();
  const totalOnPage = orders?.length || 0;
  const shown = filteredOrders.length;
  const isFiltered = shown !== totalOnPage;
  // An empty page under a status or payment-method filter is "nothing matches",
  // not "no orders yet": those filters ran on the server, before paging.
  const hasNoOrdersAtAll = fetchComplete && totalOnPage === 0 && listFilters.active === 0;
  const noServerMatch = fetchComplete && totalOnPage === 0 && listFilters.active > 0;
  const activeStatusCfg = STATUS_FILTERS.find((s) => s.key === statusFilter);
  const activeStatusLabel = activeStatusCfg?.match
    ? activeStatusCfg.label
    : listFilters.delivery.map(statusLabelOf).join(", ");
  const hiddenByFilters = fetchComplete && totalOnPage > 0 && shown === 0;

  const copyId = (id) => {
    const done = () => {
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1400);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(id).then(done).catch(() => {});
    }
  };

  const resetFilters = () => {
    const all = {
      mainStore: true,
      ...outlets.reduce((acc, o) => ({ ...acc, [o._id]: true }), {}),
    };
    setFilters(all);
    setSelectedOutlet("all");
    setSearch("");
    listFilters.clear();
    saveFilterState(all, "all");
  };

  const openInHouseOrder = () =>
    window.dispatchEvent(new CustomEvent("openInHouseOrderModal"));

  const statCards = [
    {
      key: "action",
      label: "Needs action",
      value: stats.action,
      icon: "fa-bell",
      bg: C.amberSoft,
      color: "#b06d00",
    },
    {
      key: "flight",
      label: "On the way",
      value: stats.flight,
      icon: "fa-motorcycle",
      bg: C.blueSoft,
      color: C.blueDeep,
    },
    {
      key: "done",
      label: "Completed",
      value: stats.done,
      icon: "fa-check-double",
      bg: C.tealSoft,
      color: "#00806e",
    },
    {
      key: "value",
      label: "Value on page",
      value: stats.value.toLocaleString(),
      prefix: "UGX",
      icon: "fa-coins",
      bg: "#f1f0fb",
      color: "#5b4bc4",
    },
  ];

  const locationSummary =
    outlets.length > 0
      ? `Main store and ${outlets.length} outlet${outlets.length > 1 ? "s" : ""}`
      : "Main store";

  const header = (
    <div className={classes.headerBand}>
      <div className={classes.headerGlow} />
      <div className={classes.headerLeft}>
        <div className={classes.headerMark}>
          <i className="fas fa-receipt" />
        </div>
        <div>
          <div className={classes.headerEyebrow}>
            {user?.result?.businessName || "Restaurant"}
          </div>
          <div className={classes.headerTitle}>Orders</div>
          <div className={classes.headerSub}>
            {locationSummary}
            {lastUpdate ? ` · updated ${relativeDate(lastUpdate).replace("Today ", "")}` : ""}
          </div>
        </div>
      </div>
      <div className={classes.headerRight}>
        <span className={classes.livePill}>
          <span
            className={`${classes.liveDot} ${live ? "" : classes.liveDotOff}`}
          />
          {live ? "Live" : "Reconnecting"}
        </span>
        <Tooltip
          title="Keep the location filters you pick here the next time you open this page"
          arrow
        >
          <span>
            <FormControlLabel
              control={
                <Switch
                  checked={persistFilters}
                  onChange={handlePersistToggle}
                  size="small"
                  className={classes.headerSwitch}
                />
              }
              label={
                <span className={classes.rememberLabel}>Remember filters</span>
              }
              style={{ marginRight: 0 }}
            />
          </span>
        </Tooltip>
      </div>
    </div>
  );

  if (error) {
    return (
      <Container className={classes.container}>
        {header}
        <Alert severity="error" className={classes.errorAlert}>
          We could not load your orders. {String(error)}
        </Alert>
      </Container>
    );
  }

  /* Skeleton rows instead of a full-height spinner: the page keeps its shape
     while loading, so nothing jumps when the data lands. */
  if (loading && !fetchComplete) {
    return (
      <Container className={classes.container}>
        {header}
        <div className={classes.statRow}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={classes.statCard}>
              <div className={classes.statIcon} style={{ background: "#eef2f7" }} />
              <div style={{ flex: 1 }}>
                <div className={classes.skelBar} style={{ width: "45%" }} />
                <div
                  className={classes.skelBar}
                  style={{ width: "70%", marginTop: 8, height: 7 }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className={classes.tableWrap} style={{ background: "#fff" }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={classes.skelRow}>
              <div className={classes.skelBar} style={{ width: 70 }} />
              <div className={classes.skelBar} style={{ width: 90 }} />
              <div className={classes.skelBar} style={{ width: 140, flex: 1 }} />
              <div className={classes.skelBar} style={{ width: 80 }} />
              <div className={classes.skelBar} style={{ width: 100 }} />
            </div>
          ))}
        </div>
      </Container>
    );
  }

  return (
    <Container className={classes.container}>
      {header}

      {!hasNoOrdersAtAll && (
        <div className={classes.statRow}>
          {statCards.map((s) => (
            <div key={s.key} className={classes.statCard}>
              <div
                className={classes.statIcon}
                style={{ background: s.bg, color: s.color }}
              >
                <i className={`fas ${s.icon}`} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className={classes.statValue}>
                  {s.prefix && (
                    <span
                      style={{
                        fontSize: "0.72rem",
                        color: C.textLight,
                        fontWeight: 700,
                        marginRight: 4,
                      }}
                    >
                      {s.prefix}
                    </span>
                  )}
                  {s.value}
                </div>
                <div className={classes.statLabel}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Nothing to filter when the vendor has no orders at all — the bar would
          be four dead controls above an empty panel. */}
      {!hasNoOrdersAtAll && (
      <Card className={classes.filterCard} elevation={0}>
        <div className={classes.filterContainer}>
          <Typography variant="subtitle2" className={classes.filterTitle}>
            Filter by
          </Typography>

          {pinned ? (
            <Typography variant="body2" style={{ fontSize: "0.82rem", color: C.textMid }}>
              Showing orders for {activeLocation?.name || "this branch"}
            </Typography>
          ) : (
          <>
          <FormControl className={classes.formControl} size="small">
            <Select
              value={selectedOutlet}
              onChange={handleOutletChange}
              variant="outlined"
              fullWidth
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected === "") {
                  return "Select Location";
                }

                // Get all checked filters
                const checkedFilters = Object.entries(filters).filter(
                  ([_, value]) => value
                );
                const totalOutlets = getUniqueOutlets().length + 1;

                if (checkedFilters.length === totalOutlets) {
                  return "All Locations";
                }

                // Render multiple selected options
                return (
                  <div className={classes.multipleSelected}>
                    {showMain && filters.mainStore && (
                      <Chip
                        size="small"
                        label="Main Store"
                        className={classes.locationChip}
                        style={chipStyle(LOCATION_RED, true)}
                      />
                    )}
                    {getUniqueOutlets().map(
                      (outlet) =>
                        filters[outlet._id] && (
                          <Chip
                            key={outlet._id}
                            size="small"
                            label={outlet.name}
                            className={classes.locationChip}
                            style={chipStyle(LOCATION_TEAL, true)}
                          />
                        )
                    )}
                  </div>
                );
              }}
            >
              <MenuItem value="all" className={classes.menuItem}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Chip
                    size="small"
                    label="All Locations"
                    className={classes.locationChip}
                    style={chipStyle(LOCATION_NAVY, selectedOutlet === "all")}
                  />
                  <span
                    className={`${classes.orderCount} ${
                      selectedOutlet === "all" ? classes.selectedOrderCount : ""
                    }`}
                  >
                    {orders?.length || 0}
                  </span>
                </Box>
              </MenuItem>
              {showMain && (
              <MenuItem value="mainStore" className={classes.menuItem}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Chip
                    size="small"
                    label="Main Store"
                    className={classes.locationChip}
                    style={{
                      ...chipStyle(LOCATION_RED, selectedOutlet === "mainStore"),
                      marginRight: "8px",
                    }}
                  />
                  <span
                    className={`${classes.orderCount} ${selectedOutlet === "mainStore"
                      ? classes.selectedOrderCount
                      : ""
                      }`}
                  >
                    {getStoreCounts().mainStore || 0}
                  </span>
                </Box>
              </MenuItem>
              )}
              {getUniqueOutlets().map((outlet) => (
                <MenuItem
                  key={outlet._id}
                  value={outlet._id}
                  className={classes.menuItem}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Chip
                      size="small"
                      label={outlet.name}
                      className={classes.locationChip}
                      style={chipStyle(LOCATION_TEAL, selectedOutlet === outlet._id)}
                    />
                    <span
                      className={`${classes.orderCount} ${selectedOutlet === outlet._id
                        ? classes.selectedOrderCount
                        : ""
                        }`}
                    >
                      {getStoreCounts()[outlet._id] || 0}
                    </span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box className={classes.checkboxGroup}>
            {showMain && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.mainStore}
                  onChange={handleFilterChange}
                  name="mainStore"
                  color="primary"
                  size="small"
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" style={{ fontSize: "0.82rem" }}>
                    Main Store
                  </Typography>
                  <span
                    className={`${classes.orderCount} ${filters.mainStore ? classes.selectedOrderCount : ""
                      }`}
                  >
                    {getStoreCounts().mainStore || 0}
                  </span>
                </Box>
              }
            />
            )}
            {getUniqueOutlets().map((outlet) => (
              <FormControlLabel
                key={outlet._id}
                control={
                  <Checkbox
                    checked={filters[outlet._id] || false}
                    onChange={handleFilterChange}
                    name={outlet._id}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="body2"
                      style={{ fontSize: "0.82rem" }}
                    >
                      {outlet.name}
                    </Typography>
                    <span
                      className={`${classes.orderCount} ${filters[outlet._id] ? classes.selectedOrderCount : ""
                        }`}
                    >
                      {getStoreCounts()[outlet._id] || 0}
                    </span>
                  </Box>
                }
              />
            ))}
          </Box>
          </>
          )}

          <FormControl
            className={`${classes.formControl} ${classes.statusControl}`}
            size="small"
          >
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              variant="outlined"
              fullWidth
            >
              {STATUS_FILTERS.map((s) => (
                <MenuItem key={s.key} value={s.key} className={classes.menuItem}>
                  {s.label}
                </MenuItem>
              ))}
              {statusFilter === CUSTOM_STATUS_KEY && (
                <MenuItem value={CUSTOM_STATUS_KEY} className={classes.menuItem}>
                  {listFilters.delivery.map(statusLabelOf).join(", ")}
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {listFilters.method.length > 0 && (
            <span className="lk-orderfilter-chip">
              Paid by {listFilters.method.join(", ")}
              <button
                type="button"
                onClick={() => listFilters.setMethod([])}
                aria-label="Remove payment method filter"
                title="Remove"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </span>
          )}

          <div className={classes.searchWrap}>
            <i
              className="fas fa-search"
              style={{ fontSize: 12, color: C.textLight }}
            />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Order id, customer, address"
              className={classes.searchInput}
              inputProps={{ "aria-label": "Search orders on this page" }}
            />
            {search && (
              <button
                type="button"
                className={classes.clearSearch}
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>
        </div>

        {/* Says out loud that filtering is page-scoped. The old bar left a
            vendor to infer it from a suddenly-empty table. */}
        <div className={classes.resultLine}>
          <span>
            Showing <strong style={{ color: C.text }}>{shown}</strong> of{" "}
            {totalOnPage} order{totalOnPage === 1 ? "" : "s"} on this page
            {numberOfPages > 1 ? ` · page ${currentPage} of ${numberOfPages}` : ""}
          </span>
          {isFiltered && (
            <button
              type="button"
              className={classes.resetLink}
              onClick={resetFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      </Card>
      )}

      {hasNoOrdersAtAll ? (
        <div className={classes.emptyPanel}>
          <EmptyOrdersArt />
          <div className={classes.emptyTitle}>No orders yet</div>
          <div className={classes.emptyBody}>
            When a customer places an order it lands here instantly, and your
            team is alerted. You can also ring one up yourself at the counter.
          </div>
          <button
            type="button"
            className={classes.emptyPrimary}
            onClick={openInHouseOrder}
          >
            <i className="fas fa-plus" />
            New in-house order
          </button>
          <div className={classes.channelRow}>
            {[
              { icon: "fa-mobile-alt", label: "App" },
              { icon: "fa-globe", label: "Web" },
              { icon: "fa-comment-dots", label: "WhatsApp" },
              { icon: "fa-cash-register", label: "Counter" },
            ].map((ch) => (
              <span key={ch.label} className={classes.channelPill}>
                <i className={`fas ${ch.icon}`} style={{ fontSize: 10 }} />
                {ch.label}
              </span>
            ))}
          </div>
        </div>
      ) : noServerMatch ? (
        <div className={classes.emptyPanel}>
          <FilteredEmptyArt />
          <div className={classes.emptyTitle}>
            No {activeStatusLabel ? `${activeStatusLabel.toLowerCase()} ` : ""}orders
            {listFilters.method.length ? ` paid by ${listFilters.method.join(", ")}` : ""}
          </div>
          <div className={classes.emptyBody}>
            Nothing is at this status right now. Choose Any status to see every order.
          </div>
          <button
            type="button"
            className={classes.emptyGhost}
            onClick={listFilters.clear}
          >
            Show all orders
          </button>
        </div>
      ) : hiddenByFilters ? (
        <div className={classes.emptyPanel}>
          <FilteredEmptyArt />
          <div className={classes.emptyTitle}>No orders match these filters</div>
          <div className={classes.emptyBody}>
            {totalOnPage} order{totalOnPage === 1 ? " is" : "s are"} on this page,
            but your location or search filter hides{" "}
            {totalOnPage === 1 ? "it" : "them all"}.
          </div>
          <button
            type="button"
            className={classes.emptyGhost}
            onClick={resetFilters}
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className={classes.tableWrap}>
          <TableContainer
            component={Paper}
            elevation={0}
            className={classes.tableContainer}
          >
            <Table className={classes.table} stickyHeader aria-label="Restaurant orders">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Order</TableCell>
                  <TableCell className={classes.tableHeadCell}>Store</TableCell>
                  <TableCell className={classes.tableHeadCell}>Customer</TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">
                    Total
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Delivery address
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} align="center">
                    Method
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} align="center">
                    Payment
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Delivery
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>Placed</TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => {
                  const isMain = order.displayLocation === "mainStore";
                  const accent = isMain ? LOCATION_RED : LOCATION_TEAL;
                  const customer = getCustomerName(order);
                  const address = getDeliveryAddress(order);
                  return (
                    <TableRow key={order._id} className={classes.tableRow}>
                      <TableCell
                        className={`${classes.cell} ${classes.accentCell}`}
                        component="th"
                        scope="row"
                      >
                        <span
                          className={classes.accentBar}
                          style={{ background: accent }}
                        />
                        <Tooltip title={`${order._id} — click to copy`} arrow>
                          <button
                            type="button"
                            className={classes.idButton}
                            onClick={() => copyId(order._id)}
                          >
                            {shortId(order._id)}
                            {copiedId === order._id ? (
                              <span className={classes.copiedTag}>COPIED</span>
                            ) : (
                              <i
                                className="far fa-copy"
                                style={{ fontSize: 10, opacity: 0.45 }}
                              />
                            )}
                          </button>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Chip
                          size="small"
                          label={
                            isMain
                              ? "Main Store"
                              : order.displayLocationName ||
                                order.orderItems[0]?.outletId?.name
                          }
                          className={classes.locationChip}
                          style={chipStyle(accent, true)}
                          title={
                            isMain
                              ? "Main store"
                              : order.orderItems[0]?.outletId?.address || ""
                          }
                        />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <span className={classes.customerCell}>
                          <span className={classes.avatar}>
                            {String(customer).trim().charAt(0) || "?"}
                          </span>
                          {customer}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        <span className={classes.money}>
                          <span className={classes.moneyUnit}>UGX</span>
                          {Number(order.totalPrice || 0).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <div className={classes.addressCell} title={address}>
                          {address}
                        </div>
                      </TableCell>
                      <TableCell className={classes.cell} align="center">
                        <span className={classes.methodPill}>
                          {order.paymentMethod || "—"}
                        </span>
                      </TableCell>
                      {/* Paid/unpaid was missing entirely from this table — a
                          vendor could not tell a settled order from an unpaid one
                          without opening it. Derived from the canonical fields via
                          utilities/paymentStatus.js so it matches the details page. */}
                      <TableCell className={classes.cell} align="center">
                        <PaymentBadge order={order} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <DeliveryStatusBadge status={order.delivery_status} />
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.dateCell}`}>
                        <span title={new Date(order.createdAt).toLocaleString()}>
                          {relativeDate(order.createdAt)}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        <button
                          type="button"
                          className={classes.manageBtn}
                          onClick={() =>
                            history.push(
                              `/admin/restVenOrderDetails/${order._id}`
                            )
                          }
                        >
                          Manage
                          <i className="fas fa-arrow-right" style={{ fontSize: 10 }} />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={numberOfPages * 10}
            page={currentPage - 1}
            onPageChange={(event, newPage) =>
              dispatch(fetchRestaurantVendorOrders(vendorId, newPage + 1, 10, bookOpts))
            }
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
            className={classes.pagination}
          />
        </div>
      )}
    </Container>
  );
};

export default RestaurantOrderTable;
